export const ROUTE_EPS = 0.01;

export function clampInLoc(loc, x, y, margin) {
  return {
    x: Math.max(loc.x + margin, Math.min(loc.x + loc.width - margin, x)),
    y: Math.max(loc.y + margin, Math.min(loc.y + loc.height - margin, y)),
  };
}

const approxEq = (a, b, eps = ROUTE_EPS) => Math.abs(a - b) < eps;
const isHorizontal = (s, eps = ROUTE_EPS) => approxEq(s.y1, s.y2, eps);
const inRange = (v, a, b, eps = ROUTE_EPS) => v >= Math.min(a, b) - eps && v <= Math.max(a, b) + eps;

export function segmentRelation(a, b, eps = ROUTE_EPS) {
  const ah = isHorizontal(a, eps);
  const bh = isHorizontal(b, eps);

  if (ah && bh) {
    if (!approxEq(a.y1, b.y1, eps)) return "none";
    const start = Math.max(Math.min(a.x1, a.x2), Math.min(b.x1, b.x2));
    const end = Math.min(Math.max(a.x1, a.x2), Math.max(b.x1, b.x2));
    if (end < start - eps) return "none";
    if (Math.abs(end - start) <= eps) return "touch";
    return "overlap";
  }

  if (!ah && !bh) {
    if (!approxEq(a.x1, b.x1, eps)) return "none";
    const start = Math.max(Math.min(a.y1, a.y2), Math.min(b.y1, b.y2));
    const end = Math.min(Math.max(a.y1, a.y2), Math.max(b.y1, b.y2));
    if (end < start - eps) return "none";
    if (Math.abs(end - start) <= eps) return "touch";
    return "overlap";
  }

  const h = ah ? a : b;
  const v = ah ? b : a;
  const x = v.x1;
  const y = h.y1;
  if (!inRange(x, h.x1, h.x2, eps) || !inRange(y, v.y1, v.y2, eps)) return "none";
  const isEndpoint = (seg, px, py) =>
    (approxEq(seg.x1, px, eps) && approxEq(seg.y1, py, eps)) ||
    (approxEq(seg.x2, px, eps) && approxEq(seg.y2, py, eps));
  return isEndpoint(h, x, y) || isEndpoint(v, x, y) ? "touch" : "cross";
}

export function segmentIntersectsRect(seg, rect, eps = ROUTE_EPS) {
  const minX = rect.x;
  const maxX = rect.x + rect.width;
  const minY = rect.y;
  const maxY = rect.y + rect.height;
  if (isHorizontal(seg, eps)) {
    if (seg.y1 < minY - eps || seg.y1 > maxY + eps) return false;
    const sx1 = Math.min(seg.x1, seg.x2);
    const sx2 = Math.max(seg.x1, seg.x2);
    return sx2 >= minX - eps && sx1 <= maxX + eps;
  }
  if (seg.x1 < minX - eps || seg.x1 > maxX + eps) return false;
  const sy1 = Math.min(seg.y1, seg.y2);
  const sy2 = Math.max(seg.y1, seg.y2);
  return sy2 >= minY - eps && sy1 <= maxY + eps;
}

const pointKey = (x, y, eps = ROUTE_EPS) => `${Math.round(x / eps)}:${Math.round(y / eps)}`;
const segmentKey = (s, eps = ROUTE_EPS) => {
  const a = pointKey(s.x1, s.y1, eps);
  const b = pointKey(s.x2, s.y2, eps);
  return a < b ? `${a}|${b}` : `${b}|${a}`;
};

export function evaluateRouteSegments(segments, points, otherRouteSegments, otherRouteRects, eps = ROUTE_EPS) {
  const segs = segments || [];
  let selfCross = 0;
  let selfOverlap = 0;
  let selfTouch = 0;

  for (let i = 0; i < segs.length; i++) {
    for (let j = i + 1; j < segs.length; j++) {
      if (j === i + 1) continue;
      const rel = segmentRelation(segs[i], segs[j], eps);
      if (rel === "cross") selfCross++;
      else if (rel === "overlap") selfOverlap++;
      else if (rel === "touch") selfTouch++;
    }
  }

  const pointVisits = new Map();
  (points || []).forEach(([x, y], idx) => {
    const key = pointKey(x, y, eps);
    if (!pointVisits.has(key)) pointVisits.set(key, []);
    pointVisits.get(key).push(idx);
  });
  let repeatedVertex = 0;
  pointVisits.forEach((idxs) => {
    if (idxs.length <= 1) return;
    for (let i = 1; i < idxs.length; i++) {
      if (idxs[i] - idxs[i - 1] > 1) repeatedVertex++;
    }
  });

  const segmentVisits = new Map();
  segs.forEach((s) => {
    const key = segmentKey(s, eps);
    segmentVisits.set(key, (segmentVisits.get(key) || 0) + 1);
  });
  let repeatedSegment = 0;
  segmentVisits.forEach((count) => {
    if (count > 1) repeatedSegment += count - 1;
  });

  let otherConflicts = 0;
  for (const s of segs) {
    for (const os of otherRouteSegments) {
      if (segmentRelation(s, os, eps) !== "none") otherConflicts++;
    }
  }
  let otherAreaConflicts = 0;
  for (const s of segs) {
    for (const r of otherRouteRects) {
      if (segmentIntersectsRect(s, r, eps)) otherAreaConflicts++;
    }
  }

  let selfBacktrack = 0;
  for (let i = 0; i < segs.length - 1; i++) {
    const s1 = segs[i];
    const s2 = segs[i + 1];
    const s1h = isHorizontal(s1, eps);
    const s2h = isHorizontal(s2, eps);
    if (s1h && s2h && approxEq(s1.y1, s2.y1, eps)) {
      const dir1 = Math.sign(s1.x2 - s1.x1);
      const dir2 = Math.sign(s2.x2 - s2.x1);
      if (dir1 !== 0 && dir2 !== 0 && dir1 !== dir2) {
        const start = Math.max(Math.min(s1.x1, s1.x2), Math.min(s2.x1, s2.x2));
        const end = Math.min(Math.max(s1.x1, s1.x2), Math.max(s2.x1, s2.x2));
        if (end > start + eps) selfBacktrack++;
      }
    }
    if (!s1h && !s2h && approxEq(s1.x1, s2.x1, eps)) {
      const dir1 = Math.sign(s1.y2 - s1.y1);
      const dir2 = Math.sign(s2.y2 - s2.y1);
      if (dir1 !== 0 && dir2 !== 0 && dir1 !== dir2) {
        const start = Math.max(Math.min(s1.y1, s1.y2), Math.min(s2.y1, s2.y2));
        const end = Math.min(Math.max(s1.y1, s1.y2), Math.max(s2.y1, s2.y2));
        if (end > start + eps) selfBacktrack++;
      }
    }
  }

  let junctionBranches = 0;
  const endpointDegree = new Map();
  const bumpEndpoint = (x, y) => {
    const key = pointKey(x, y, eps);
    endpointDegree.set(key, (endpointDegree.get(key) || 0) + 1);
  };
  segs.forEach((s) => {
    bumpEndpoint(s.x1, s.y1);
    bumpEndpoint(s.x2, s.y2);
  });
  endpointDegree.forEach((count) => {
    if (count > 2) junctionBranches++;
  });

  const selfConflicts = selfCross + selfOverlap;
  const branchConflicts = selfTouch + repeatedVertex + repeatedSegment + selfBacktrack + junctionBranches;
  const totalOtherConflicts = otherConflicts + otherAreaConflicts;
  const turns = segs.reduce((count, s, i) => {
    if (i === 0) return 0;
    return count + (isHorizontal(segs[i - 1], eps) !== isHorizontal(s, eps) ? 1 : 0);
  }, 0);
  const length = segs.reduce((sum, s) => sum + Math.abs(s.x1 - s.x2) + Math.abs(s.y1 - s.y2), 0);

  return {
    selfConflicts,
    otherConflicts: totalOtherConflicts,
    branchConflicts,
    turns,
    length,
    feasible: selfConflicts === 0 && totalOtherConflicts === 0 && branchConflicts === 0,
    penaltyCount: selfConflicts + totalOtherConflicts + branchConflicts,
  };
}

function wpKey(wp) {
  return `${Math.round(wp.x / ROUTE_EPS)}:${Math.round(wp.y / ROUTE_EPS)}`;
}

function uniqueWaypoints(list) {
  const seen = new Set();
  const out = [];
  list.forEach((wp) => {
    const key = wpKey(wp);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(wp);
  });
  return out;
}

function sampleWaypointsInRect(loc, margin) {
  const cx = loc.x + loc.width / 2;
  const cy = loc.y + loc.height / 2;
  const xmin = loc.x + margin;
  const xmax = loc.x + loc.width - margin;
  const ymin = loc.y + margin;
  const ymax = loc.y + loc.height - margin;
  if (xmax < xmin || ymax < ymin) return [{ x: cx, y: cy }];
  return uniqueWaypoints([
    clampInLoc(loc, cx, cy, margin),
    clampInLoc(loc, cx, ymin, margin),
    clampInLoc(loc, cx, ymax, margin),
    clampInLoc(loc, xmin, cy, margin),
    clampInLoc(loc, xmax, cy, margin),
  ]);
}

function makeSeg(x1, y1, x2, y2, eps = ROUTE_EPS) {
  if (Math.abs(x1 - x2) < eps && Math.abs(y1 - y2) < eps) return null;
  if (Math.abs(x1 - x2) >= eps && Math.abs(y1 - y2) >= eps) return null;
  return { x1, y1, x2, y2 };
}

function edgeKey(idA, idB) {
  return `${String(idA)}|${String(idB)}`;
}

function pathPointsFromSegs(fromWp, segs) {
  const pts = [[fromWp.x, fromWp.y]];
  segs.forEach((s) => pts.push([s.x2, s.y2]));
  return pts;
}

function mergeEdgePaths(orderedIds, edgePaths) {
  const allPoints = [];
  for (let i = 0; i < orderedIds.length - 1; i++) {
    const pts = edgePaths.get(edgeKey(orderedIds[i], orderedIds[i + 1]));
    if (!pts || !pts.length) continue;
    pts.forEach((p, pi) => {
      if (allPoints.length && pi === 0) {
        const last = allPoints[allPoints.length - 1];
        if (Math.abs(last[0] - p[0]) < ROUTE_EPS && Math.abs(last[1] - p[1]) < ROUTE_EPS) return;
      }
      allPoints.push(p);
    });
  }
  const segments = [];
  for (let i = 0; i < allPoints.length - 1; i++) {
    const seg = makeSeg(allPoints[i][0], allPoints[i][1], allPoints[i + 1][0], allPoints[i + 1][1]);
    if (seg) segments.push(seg);
  }
  return { points: allPoints, segments };
}

export function buildCanvasSegmentsFromEdgePaths(orderedIds, edgePaths) {
  const segments = [];
  for (let i = 0; i < orderedIds.length - 1; i++) {
    const idA = orderedIds[i];
    const idB = orderedIds[i + 1];
    const pts = edgePaths.get(edgeKey(idA, idB));
    if (!pts || pts.length < 2) continue;
    segments.push({
      key: `${idA}-${idB}`,
      points: pts.map(([x, y]) => [x, y]),
    });
  }
  return segments;
}

function listConnectionOptions(fromWp, locFrom, locTo, allowCorner, margin) {
  const options = [];
  const pushOption = (endWp, segs) => {
    const validSegs = segs.filter(Boolean);
    if (!validSegs.length) return;
    options.push({ endWp, segs: validSegs, pathPoints: pathPointsFromSegs(fromWp, validSegs) });
  };

  const yMin = locTo.y + margin;
  const yMax = locTo.y + locTo.height - margin;
  if (fromWp.y >= yMin - ROUTE_EPS && fromWp.y <= yMax + ROUTE_EPS) {
    const endWp = clampInLoc(locTo, locTo.x + locTo.width / 2, fromWp.y, margin);
    pushOption(endWp, [makeSeg(fromWp.x, fromWp.y, endWp.x, endWp.y)]);
  }

  const xMin = locTo.x + margin;
  const xMax = locTo.x + locTo.width - margin;
  if (fromWp.x >= xMin - ROUTE_EPS && fromWp.x <= xMax + ROUTE_EPS) {
    const endWp = clampInLoc(locTo, fromWp.x, locTo.y + locTo.height / 2, margin);
    pushOption(endWp, [makeSeg(fromWp.x, fromWp.y, endWp.x, endWp.y)]);
  }

  if (locFrom) {
    const sharedYMin = Math.max(locFrom.y + margin, locTo.y + margin);
    const sharedYMax = Math.min(locFrom.y + locFrom.height - margin, locTo.y + locTo.height - margin);
    if (sharedYMin <= sharedYMax) {
      [sharedYMin, (sharedYMin + sharedYMax) / 2, sharedYMax].forEach((sy) => {
        const endWp = clampInLoc(locTo, locTo.x + locTo.width / 2, sy, margin);
        if (approxEq(fromWp.y, sy, ROUTE_EPS)) {
          pushOption(endWp, [makeSeg(fromWp.x, fromWp.y, endWp.x, endWp.y)]);
        } else if (allowCorner) {
          pushOption(endWp, [
            makeSeg(fromWp.x, fromWp.y, fromWp.x, sy),
            makeSeg(fromWp.x, sy, endWp.x, endWp.y),
          ]);
          pushOption(endWp, [
            makeSeg(fromWp.x, fromWp.y, endWp.x, fromWp.y),
            makeSeg(endWp.x, fromWp.y, endWp.x, endWp.y),
          ]);
        }
      });
    }

    const sharedXMin = Math.max(locFrom.x + margin, locTo.x + margin);
    const sharedXMax = Math.min(locFrom.x + locFrom.width - margin, locTo.x + locTo.width - margin);
    if (sharedXMin <= sharedXMax) {
      [sharedXMin, (sharedXMin + sharedXMax) / 2, sharedXMax].forEach((sx) => {
        const endWp = clampInLoc(locTo, sx, locTo.y + locTo.height / 2, margin);
        if (approxEq(fromWp.x, sx, ROUTE_EPS)) {
          pushOption(endWp, [makeSeg(fromWp.x, fromWp.y, endWp.x, endWp.y)]);
        } else if (allowCorner) {
          pushOption(endWp, [
            makeSeg(fromWp.x, fromWp.y, sx, fromWp.y),
            makeSeg(sx, fromWp.y, sx, endWp.y),
          ]);
          pushOption(endWp, [
            makeSeg(fromWp.x, fromWp.y, fromWp.x, endWp.y),
            makeSeg(fromWp.x, endWp.y, endWp.x, endWp.y),
          ]);
        }
      });
    }
  }

  if (!allowCorner) return options;

  sampleWaypointsInRect(locTo, margin).forEach((endWp) => {
    pushOption(endWp, [
      makeSeg(fromWp.x, fromWp.y, endWp.x, fromWp.y),
      makeSeg(endWp.x, fromWp.y, endWp.x, endWp.y),
    ]);
    pushOption(endWp, [
      makeSeg(fromWp.x, fromWp.y, fromWp.x, endWp.y),
      makeSeg(fromWp.x, endWp.y, endWp.x, endWp.y),
    ]);
  });

  return options;
}

function isExtensionValid(orderedSoFar, edgePaths, ctx) {
  const { points, segments } = mergeEdgePaths(orderedSoFar, edgePaths);
  const score = evaluateRouteSegments(segments, points, ctx.otherRouteSegments, ctx.otherRouteRects);
  return score.feasible;
}

function extendRoutePath(orderedIds, idx, currentWp, waypointsById, edgePaths, ctx) {
  if (idx === orderedIds.length - 1) {
    const { points, segments } = mergeEdgePaths(orderedIds, edgePaths);
    const score = evaluateRouteSegments(segments, points, ctx.otherRouteSegments, ctx.otherRouteRects);
    if (!score.feasible) return null;
    return { waypointsById, edgePaths, points, segments, score };
  }

  const nextId = orderedIds[idx + 1];
  const fromId = orderedIds[idx];
  const locFrom = ctx.rectById.get(String(fromId));
  const locTo = ctx.rectById.get(String(nextId));
  if (!locTo) return null;

  const options = listConnectionOptions(currentWp, locFrom, locTo, ctx.allowCorner, ctx.margin);
  for (let oi = 0; oi < options.length; oi++) {
    const opt = options[oi];
    const nextWaypoints = new Map(waypointsById);
    nextWaypoints.set(String(nextId), opt.endWp);
    const nextEdgePaths = new Map(edgePaths);
    nextEdgePaths.set(edgeKey(fromId, nextId), opt.pathPoints);
    const orderedSoFar = orderedIds.slice(0, idx + 2);
    if (!isExtensionValid(orderedSoFar, nextEdgePaths, ctx)) {
      continue;
    }

    const found = extendRoutePath(
      orderedIds,
      idx + 1,
      opt.endWp,
      nextWaypoints,
      nextEdgePaths,
      ctx
    );
    if (found) return found;
  }
  return null;
}

export function assessRouteOrder(orderedIds, ctx) {
  const built = buildConflictAwareRoute(orderedIds, { ...ctx, allowCorner: true });
  const score = built.score || {
    feasible: false,
    penaltyCount: 0,
    selfConflicts: 0,
    otherConflicts: 0,
    branchConflicts: 0,
    turns: 0,
  };
  return { built, score, feasible: built.feasible };
}

export function buildConflictAwareRoute(orderedIds, ctx) {
  if (!orderedIds || orderedIds.length < 2) {
    return { feasible: false, points: [], segments: [], entries: [], score: null };
  }

  const firstId = orderedIds[0];
  const firstLoc = ctx.rectById.get(String(firstId));
  if (!firstLoc) {
    return { feasible: false, points: [], segments: [], entries: [], score: null };
  }

  const startCandidates = sampleWaypointsInRect(firstLoc, ctx.margin);
  for (let si = 0; si < startCandidates.length; si++) {
    const startWp = startCandidates[si];
    const waypointsById = new Map([[String(firstId), startWp]]);
    const found = extendRoutePath(
      orderedIds,
      0,
      startWp,
      waypointsById,
      new Map(),
      ctx
    );
    if (found) {
      const entries = orderedIds.map((id) => {
        const wp = found.waypointsById.get(String(id));
        return {
          locationId: id,
          centerX: wp?.x ?? 0,
          centerY: wp?.y ?? 0,
          wx: wp?.x ?? 0,
          wy: wp?.y ?? 0,
        };
      });
      return {
        feasible: true,
        points: found.points,
        segments: found.segments,
        entries,
        score: found.score,
        waypointsById: found.waypointsById,
        edgePaths: found.edgePaths,
      };
    }
  }

  return { feasible: false, points: [], segments: [], entries: [], score: null };
}

export function buildCanvasRouteSegments(entries) {
  return entries.slice(0, -1).map((A, i) => {
    const B = entries[i + 1];
    const straight = Math.abs(A.wx - B.wx) < 0.5 || Math.abs(A.wy - B.wy) < 0.5;
    return {
      key: `${A.locationId}-${B.locationId}`,
      points: straight
        ? [[A.wx, A.wy], [B.wx, B.wy]]
        : [[A.wx, A.wy], [B.wx, A.wy], [B.wx, B.wy]],
    };
  });
}

export function isScoreBetter(a, b) {
  if (!b) return true;
  if (a.feasible !== b.feasible) return a.feasible;
  if (!a.feasible && a.penaltyCount !== b.penaltyCount) return a.penaltyCount < b.penaltyCount;
  if (!a.feasible && a.selfConflicts !== b.selfConflicts) return a.selfConflicts < b.selfConflicts;
  if (!a.feasible && a.otherConflicts !== b.otherConflicts) return a.otherConflicts < b.otherConflicts;
  if (!a.feasible && a.branchConflicts !== b.branchConflicts) return a.branchConflicts < b.branchConflicts;
  if (a.turns !== b.turns) return a.turns < b.turns;
  return a.length < b.length - ROUTE_EPS;
}

function centerOf(rectById, id) {
  const r = rectById.get(String(id));
  if (!r) return { x: 0, y: 0 };
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 };
}

function manhattan(rectById, idA, idB) {
  const a = centerOf(rectById, idA);
  const b = centerOf(rectById, idB);
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function canStraightReach(fromWp, locTo, margin) {
  const yMin = locTo.y + margin;
  const yMax = locTo.y + locTo.height - margin;
  if (fromWp.y >= yMin - ROUTE_EPS && fromWp.y <= yMax + ROUTE_EPS) return true;
  const xMin = locTo.x + margin;
  const xMax = locTo.x + locTo.width - margin;
  return fromWp.x >= xMin - ROUTE_EPS && fromWp.x <= xMax + ROUTE_EPS;
}

function buildNearestNeighbourOrder(locationIds, startId, rectById) {
  const pool = locationIds.filter((id) => id !== startId);
  const order = [startId];
  while (pool.length) {
    const last = order[order.length - 1];
    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < pool.length; i++) {
      const d = manhattan(rectById, last, pool[i]);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    order.push(pool.splice(bestIdx, 1)[0]);
  }
  return order;
}

function buildAxisSortSeed(locationIds, rectById, horizontalFirst) {
  return [...locationIds].sort((a, b) => {
    const ca = centerOf(rectById, a);
    const cb = centerOf(rectById, b);
    if (horizontalFirst) {
      if (Math.abs(ca.y - cb.y) > 30) return ca.y - cb.y;
      return ca.x - cb.x;
    }
    if (Math.abs(ca.x - cb.x) > 30) return ca.x - cb.x;
    return ca.y - cb.y;
  });
}

function buildRowSnakeSeed(locationIds, rectById) {
  const sorted = buildAxisSortSeed(locationIds, rectById, true);
  const rows = [];
  sorted.forEach((id) => {
    const c = centerOf(rectById, id);
    const row = rows.find((r) => Math.abs(r.y - c.y) <= 30);
    if (row) row.ids.push(id);
    else rows.push({ y: c.y, ids: [id] });
  });
  rows.sort((a, b) => a.y - b.y);
  const out = [];
  rows.forEach((row, idx) => {
    const ids = [...row.ids].sort(
      (a, b) => centerOf(rectById, a).x - centerOf(rectById, b).x
    );
    if (idx % 2 === 1) ids.reverse();
    out.push(...ids);
  });
  return out;
}

function tryOrder(order, ctx) {
  const straightCtx = { ...ctx, allowCorner: false };
  let built = buildConflictAwareRoute(order, straightCtx);
  if (built.feasible) return { order, built };
  const cornerCtx = { ...ctx, allowCorner: true };
  built = buildConflictAwareRoute(order, cornerCtx);
  if (built.feasible) return { order, built };
  return null;
}

function permute(arr) {
  if (arr.length <= 1) return [arr];
  const out = [];
  arr.forEach((item, i) => {
    const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
    permute(rest).forEach((p) => out.push([item, ...p]));
  });
  return out;
}

function solveOrderBacktracking(locationIds, startId, ctx, state) {
  const order = [startId];
  const remaining = new Set(locationIds.filter((id) => id !== startId));

  const dfs = () => {
    state.explored++;
    if (state.explored > state.limit) return null;

    if (order.length > 1) {
      const partialBuilt = buildConflictAwareRoute(order, ctx);
      if (!partialBuilt.feasible) return null;
    }

    if (order.length === locationIds.length) {
      const built = buildConflictAwareRoute(order, ctx);
      return built.feasible ? { order: [...order], built } : null;
    }

    const partial = buildConflictAwareRoute(order, ctx);
    const lastWp = partial.waypointsById?.get(String(order[order.length - 1]));
    const lastId = order[order.length - 1];

    const candidates = [...remaining].sort((a, b) => {
      if (lastWp) {
        const locB = ctx.rectById.get(String(b));
        const locA = ctx.rectById.get(String(a));
        const aStraight = locA && canStraightReach(lastWp, locA, ctx.margin) ? 1 : 0;
        const bStraight = locB && canStraightReach(lastWp, locB, ctx.margin) ? 1 : 0;
        if (aStraight !== bStraight) return bStraight - aStraight;
      }
      return manhattan(ctx.rectById, lastId, a) - manhattan(ctx.rectById, lastId, b);
    });

    if (!ctx.allowCorner) {
      const filtered = candidates.filter((id) => {
        if (!lastWp) return true;
        const loc = ctx.rectById.get(String(id));
        return loc && canStraightReach(lastWp, loc, ctx.margin);
      });
      if (filtered.length) {
        candidates.length = 0;
        candidates.push(...filtered);
      }
    }

    for (let ci = 0; ci < candidates.length; ci++) {
      const nextId = candidates[ci];
      order.push(nextId);
      remaining.delete(nextId);
      const found = dfs();
      if (found) return found;
      remaining.add(nextId);
      order.pop();
    }
    return null;
  };

  return dfs();
}

export function findFeasibleRouteOrder(locationIds, ctx, options = {}) {
  const state = { explored: 0, limit: options.exploreLimit || 500000 };
  const seeds = [];
  const addSeed = (order) => {
    if (!order || order.length !== locationIds.length) return;
    const key = order.map(String).join("|");
    if (seeds.some((s) => s.key === key)) return;
    seeds.push({ key, order });
  };

  addSeed(locationIds);
  addSeed([...locationIds].reverse());
  addSeed(buildAxisSortSeed(locationIds, ctx.rectById, true));
  addSeed(buildAxisSortSeed(locationIds, ctx.rectById, false));
  addSeed(buildRowSnakeSeed(locationIds, ctx.rectById));
  locationIds.forEach((startId) => addSeed(buildNearestNeighbourOrder(locationIds, startId, ctx.rectById)));

  for (let i = 0; i < seeds.length; i++) {
    const hit = tryOrder(seeds[i].order, ctx);
    if (hit) return { ...hit, explored: state.explored, source: "seed" };
  }

  if (locationIds.length <= 8) {
    for (let si = 0; si < locationIds.length; si++) {
      const startId = locationIds[si];
      const rest = locationIds.filter((id) => id !== startId);
      const perms = permute(rest);
      for (let pi = 0; pi < perms.length; pi++) {
        const order = [startId, ...perms[pi]];
        const hit = tryOrder(order, ctx);
        if (hit) return { ...hit, explored: state.explored, source: "brute" };
      }
    }
  }

  const tryStarts = [locationIds[0], ...locationIds.slice(1)];
  for (let phase = 0; phase < 2; phase++) {
    const allowCorner = phase === 1;
    const phaseCtx = { ...ctx, allowCorner };
    for (let i = 0; i < tryStarts.length; i++) {
      const found = solveOrderBacktracking(locationIds, tryStarts[i], phaseCtx, state);
      if (found) return { ...found, explored: state.explored, source: allowCorner ? "backtrack-corner" : "backtrack-straight" };
    }
  }

  const fallbackBuilt = buildConflictAwareRoute(locationIds, { ...ctx, allowCorner: true });
  return {
    order: locationIds,
    built: fallbackBuilt,
    explored: state.explored,
    source: "none",
  };
}
